const normalizePath = (path = "") => {
  if (!path) {
    return "";
  }

  if (path === "/") {
    return "/";
  }

  return String(path).replace(/\/+$/, "");
};

const matchesRoutePath = (pathname, targetPath) =>
  normalizePath(pathname) === normalizePath(targetPath);

const normalizeRoutePrefix = (routePrefix = "") => {
  if (!routePrefix) {
    return "/";
  }

  const withLeadingSlash = routePrefix.startsWith("/")
    ? routePrefix
    : `/${routePrefix}`;

  return withLeadingSlash.endsWith("/")
    ? withLeadingSlash
    : `${withLeadingSlash}/`;
};

const getMarketRoute = (routePrefix, marketId) =>
  `${normalizeRoutePrefix(routePrefix)}${marketId}`;

const itemMatchesActivation = (item, pathname, activeMenuItems) =>
  item.activationMatchers?.some((matcher) =>
    matcher(pathname, activeMenuItems),
  ) || false;

const visitNavigationItem = (
  item,
  pathname,
  activeMenuItems,
  expandedMenuIds,
) => {
  if (!item) {
    return false;
  }

  if (item.kind === "submenu") {
    const descendantIsActive = (item.groups || []).some((group) =>
      (group.items || []).some((childItem) =>
        visitNavigationItem(
          childItem,
          pathname,
          activeMenuItems,
          expandedMenuIds,
        ),
      ),
    );
    const selfIsActive = itemMatchesActivation(item, pathname, activeMenuItems);

    if (!selfIsActive && !descendantIsActive) {
      return false;
    }

    if (item.id) {
      activeMenuItems.add(item.id);
      expandedMenuIds.add(item.id);
    }

    return true;
  }

  if (item.kind === "marketSelector") {
    const matchingMarket = (item.markets || []).find((market) =>
      matchesRoutePath(
        pathname,
        getMarketRoute(item.routePrefix, market.marketId),
      ),
    );

    if (!matchingMarket) {
      return false;
    }

    activeMenuItems.add(
      getMarketRoute(item.routePrefix, matchingMarket.marketId),
    );
    return true;
  }

  if (item.kind !== "link") {
    return false;
  }

  const selfIsActive =
    matchesRoutePath(pathname, item.to) ||
    itemMatchesActivation(item, pathname, activeMenuItems);

  if (!selfIsActive) {
    return false;
  }

  if (item.id) {
    activeMenuItems.add(item.id);
  }

  if (item.to) {
    activeMenuItems.add(item.to);
  }

  return true;
};

const buildNavigationModel = ({
  t,
  permissions,
  showMarketControl,
  handleOpenMarkets,
  handleCloseMarkets,
  handleShowAboutModel,
  login,
  unapprovedMarkCount,
  unapprovedTradeCount,
  handleLogout,
  subscribedMarkets,
  termSchedules,
  manageExternalMarkets,
}) => {
  const topLevelItems = [
    permissions.showAlbertaEnergyDaily && {
      id: "albertaenergydaily",
      kind: "link",
      sectionLabel: t("Reporting"),
      label: t("AlbertaEnergyDaily"),
      icon: "plug-circle-bolt",
      to: "/albertaenergydaily",
      component: "AlbertaPowerReport",
      name: t("AlbertaEnergyDaily"),
      config: { route: "/albertaenergydaily" },
      activationMatchers: [
        (pathname) => pathname.startsWith("/albertaenergydaily"),
      ],
    },
    permissions.showMarketData && {
      id: "marketDataParent",
      kind: "submenu",
      sectionLabel: t("Reporting"),
      label: t("MarketData"),
      icon: "chart-column",
      activationMatchers: [(pathname) => pathname.startsWith("/marketData")],
      groups: [
        {
          label: "",
          items: [
            {
              id: "marketDataParent-selector",
              kind: "marketSelector",
              markets: subscribedMarkets,
              listKey: "marketDataParent",
              component: "MarketData",
              routePrefix: "marketData/",
              namePrefix: t("MarketData"),
              manageExternalMarkets,
              parentIds: ["marketDataParent"],
              enableSearch: true,
              termSchedules,
            },
          ],
        },
      ],
    },
    permissions.tradingEnabled &&
      (permissions.showTrading ||
        permissions.showCompanyWhitelistManager ||
        permissions.showOrderReleaseLimitManager) && {
        id: "tradingParent",
        kind: "submenu",
        sectionLabel: t("Trading"),
        label: t("Trading"),
        icon: "people-arrows",
        activationMatchers: [
          (pathname) => pathname.startsWith("/trading"),
          (pathname) => pathname.startsWith("/counterparty-whitelists"),
          (pathname) => pathname.startsWith("/order-release-limits"),
        ],
        groups: [
          {
            label: "",
            items: [
              permissions.showTrading && {
                kind: "link",
                to: "/trading",
                icon: "chart-line",
                label: t("MarketScreen"),
                component: "TradingComponent",
                id: "trading",
                name: t("MarketScreen"),
                config: { route: "/trading" },
                isSubmenuItem: true,
                parentIds: ["tradingParent"],
                searchText: `${t("Trading")} ${t("MarketScreen")}`,
              },
              permissions.showCompanyWhitelistManager && {
                kind: "link",
                to: "/counterparty-whitelists",
                icon: "list-check",
                label: t("CounterpartyWhitelists"),
                component: "CompanyWhitelistPage",
                id: "counterparty-whitelists",
                name: t("CounterpartyWhitelists"),
                config: { route: "/counterparty-whitelists" },
                isSubmenuItem: true,
                parentIds: ["tradingParent"],
                searchText: `${t("Trading")} ${t("CounterpartyWhitelists")}`,
              },
              permissions.showOrderReleaseLimitManager && {
                kind: "link",
                to: "/order-release-limits",
                icon: "gauge-high",
                label: t("OrderReleaseLimits"),
                component: "OrderReleaseLimitPage",
                id: "order-release-limits",
                name: t("OrderReleaseLimits"),
                config: { route: "/order-release-limits" },
                isSubmenuItem: true,
                parentIds: ["tradingParent"],
                searchText: `${t("Trading")} ${t("OrderReleaseLimits")}`,
              },
            ].filter(Boolean),
          },
        ],
      },
    permissions.showAlbertaEnergyDaily && {
      id: "albertaelectricitydashboard",
      kind: "link",
      sectionLabel: t("Tools"),
      label: t("AlbertaElectricityDashboard"),
      icon: "bolt",
      to: "/dashboards/albertaelectricity",
      component: "DashboardContainerElectricity",
      name: t("AlbertaElectricityDashboard"),
      config: { route: "/dashboards/albertaelectricity" },
      activationMatchers: [
        (pathname) => pathname.startsWith("/dashboards/albertaelectricity"),
      ],
    },
    permissions.showAlbertaEnvironmental && {
      id: "albertaEnvironmental",
      kind: "submenu",
      sectionLabel: t("Tools"),
      label: t("AlbertaEnvironmental"),
      icon: "seedling",
      activationMatchers: [
        (pathname) => pathname.startsWith("/dashboards/albertaenvironmental"),
      ],
      groups: [
        {
          label: "",
          items: [
            {
              kind: "link",
              to: "/dashboards/albertaenvironmental",
              icon: "leaf",
              label: t("Dashboard"),
              component: "DashboardContainerEnvironmental",
              id: "albertaenvironmentaldashboard",
              name: t("AlbertaEnvironmentalDashboard"),
              config: {
                route: "/dashboards/albertaenvironmental",
              },
              isSubmenuItem: true,
              parentIds: ["albertaEnvironmental"],
              searchText: `${t("AlbertaEnvironmental")} ${t("Dashboard")}`,
            },
            {
              kind: "link",
              to: "/dashboards/albertaenvironmental/trades",
              icon: "arrow-right-arrow-left",
              label: t("Trades"),
              component: "AlbertaEnvironmentalTrades",
              id: "albertaenvironmentaltrades",
              name: t("Trades"),
              config: {
                route: "/dashboards/albertaenvironmental/trades",
              },
              isSubmenuItem: true,
              parentIds: ["albertaEnvironmental"],
              searchText: `${t("AlbertaEnvironmental")} ${t("Trades")}`,
            },
            {
              kind: "link",
              to: "/dashboards/albertaenvironmental/registry",
              icon: "book",
              label: t("Registry"),
              component: "AlbertaEnvironmentalRegistry",
              id: "albertaenvironmentalregistry",
              name: t("Registry"),
              config: {
                route: "/dashboards/albertaenvironmental/registry",
              },
              isSubmenuItem: true,
              parentIds: ["albertaEnvironmental"],
              searchText: `${t("AlbertaEnvironmental")} ${t("Registry")}`,
            },
            {
              kind: "link",
              to: "/dashboards/albertaenvironmental/issues",
              icon: "seedling",
              label: t("Issues"),
              component: "AlbertaEnvironmentalIssues",
              id: "albertaenvironmentalissues",
              name: t("Issues"),
              config: {
                route: "/dashboards/albertaenvironmental/issues",
              },
              isSubmenuItem: true,
              parentIds: ["albertaEnvironmental"],
              searchText: `${t("AlbertaEnvironmental")} ${t("Issues")}`,
            },
            {
              kind: "link",
              to: "/dashboards/albertaenvironmental/statuschanges",
              icon: "arrows-turn-to-dots",
              label: t("StatusChanges"),
              component: "AlbertaEnvironmentalStatusChanges",
              id: "albertaenvironmentalstatuschanges",
              name: t("StatusChanges"),
              config: {
                route: "/dashboards/albertaenvironmental/statuschanges",
              },
              isSubmenuItem: true,
              parentIds: ["albertaEnvironmental"],
              searchText: `${t("AlbertaEnvironmental")} ${t("StatusChanges")}`,
            },
          ],
        },
      ],
    },
    permissions.showBcEnvironmental && {
      id: "bcEnvironmental",
      kind: "submenu",
      sectionLabel: t("Tools"),
      label: t("BCEnvironmental"),
      icon: "mountain-sun",
      activationMatchers: [
        (pathname) => pathname.startsWith("/dashboards/bcEnvironmental"),
      ],
      groups: [
        {
          label: "",
          items: [
            {
              kind: "link",
              to: "/dashboards/bcEnvironmental/registry",
              icon: "book",
              label: t("BCRegistry"),
              component: "BCEnvironmentalRegistry",
              id: "bcEnvironmentalregistry",
              name: t("BCRegistry"),
              config: {
                route: "/dashboards/bcEnvironmental/registry",
              },
              isSubmenuItem: true,
              parentIds: ["bcEnvironmental"],
              searchText: `${t("BCEnvironmental")} ${t("BCRegistry")}`,
            },
            {
              kind: "link",
              to: "/dashboards/bcEnvironmental/issuances",
              icon: "seedling",
              label: t("Issuances"),
              component: "BCEnvironmentalIssuances",
              id: "bcEnvironmentalissuances",
              name: t("BCEnvironmentalIssuances"),
              config: {
                route: "/dashboards/bcEnvironmental/issuances",
              },
              isSubmenuItem: true,
              parentIds: ["bcEnvironmental"],
              searchText: `${t("BCEnvironmental")} ${t("Issuances")}`,
            },
            {
              kind: "link",
              to: "/dashboards/bcEnvironmental/retirements",
              icon: "archive",
              label: t("Retirements"),
              component: "BCEnvironmentalRetirements",
              id: "bcEnvironmentalretirements",
              name: t("BCEnvironmentalRetirements"),
              config: {
                route: "/dashboards/bcEnvironmental/retirements",
              },
              isSubmenuItem: true,
              parentIds: ["bcEnvironmental"],
              searchText: `${t("BCEnvironmental")} ${t("Retirements")}`,
            },
          ],
        },
      ],
    },
    permissions.showMarketData &&
      (permissions.showSubmitData ||
      permissions.showReviewMarks ||
      permissions.showReviewTrades) && {
      id: "manageData",
      kind: "submenu",
      sectionLabel: t("Operations"),
      label: t("ManageData"),
      icon: "database",
      activationMatchers: [
        (pathname) => pathname.startsWith("/submitData"),
        (pathname) => pathname.startsWith("/marketdata/review"),
      ],
      groups: [
        {
          label: "",
          items: [
            permissions.showSubmitData && {
              id: "submitDataParent",
              kind: "submenu",
              label: t("SubmitData"),
              icon: "pen-to-square",
              parentIds: ["manageData"],
              groups: [
                {
                  label: "",
                  items: [
                    {
                      id: "submitDataParent-selector",
                      kind: "marketSelector",
                      requiresExpandedParent: true,
                      markets: subscribedMarkets.filter(
                        (market) => market.contributor,
                      ),
                      listKey: "submitDataParent",
                      component: "SubmitDataParentAuthWrapper",
                      routePrefix: "submitData/",
                      namePrefix: t("SubmitData"),
                      manageExternalMarkets,
                      parentIds: ["manageData", "submitDataParent"],
                      enableSearch: true,
                      termSchedules,
                    },
                  ],
                },
              ],
            },
            permissions.showReviewMarks && {
              kind: "link",
              to: "/marketdata/reviewmarks",
              icon: "magnifying-glass-dollar",
              label: t("MarketDataReviewMarks"),
              badgeCount: unapprovedMarkCount,
              className: "review-count-menu-item",
              component: "ReviewMarksAuthWrapper",
              id: "reviewmarks",
              name: t("MarketDataReviewMarks"),
              config: {
                route: "/marketdata/reviewmarks",
              },
              isSubmenuItem: true,
              parentIds: ["manageData"],
              searchText: t("MarketDataReviewMarks"),
            },
            permissions.showReviewTrades && {
              kind: "link",
              to: "/marketdata/reviewtrades",
              icon: "magnifying-glass-arrow-right",
              label: t("MarketDataReviewTrades"),
              badgeCount: unapprovedTradeCount,
              className: "review-count-menu-item",
              component: "ReviewTradesAuthWrapper",
              id: "reviewtrades",
              name: t("MarketDataReviewTrades"),
              config: {
                route: "/marketdata/reviewtrades",
              },
              isSubmenuItem: true,
              parentIds: ["manageData"],
              searchText: t("MarketDataReviewTrades"),
            },
          ],
        },
      ],
    },
    permissions.authenticated &&
      permissions.showAdmin && {
        id: "admin",
        kind: "submenu",
        sectionLabel: t("Operations"),
        label: t("Admin"),
        icon: "shield",
        activationMatchers: [(pathname) => pathname.startsWith("/admin")],
        groups: [
          {
            label: "",
            items: [
              permissions.showUserAdmin && {
                kind: "link",
                to: "/admin/auth",
                icon: "users",
                label: t("UserAdmin"),
                component: "AuthAdmin",
                id: "admin/auth",
                name: t("UserAdmin"),
                config: {
                  route: "/admin/auth",
                },
                isSubmenuItem: true,
                parentIds: ["admin"],
                searchText: t("UserAdmin"),
              },
              permissions.showProductAdmin && {
                kind: "link",
                to: "/admin/products",
                icon: "coins",
                label: t("ProductAdmin"),
                component: "ProductAdmin",
                id: "admin/products",
                name: t("ProductAdmin"),
                config: {
                  route: "/admin/products",
                },
                isSubmenuItem: true,
                parentIds: ["admin"],
                searchText: t("ProductAdmin"),
              },
              permissions.showMarketAdmin && {
                kind: "link",
                to: "/admin/markets",
                icon: "store",
                label: t("MarketAdmin"),
                component: "MarketAdmin",
                id: "admin/markets",
                name: t("MarketAdmin"),
                config: {
                  route: "/admin/markets",
                },
                isSubmenuItem: true,
                parentIds: ["admin"],
                searchText: t("MarketAdmin"),
              },
              permissions.showTradingAdmin && {
                kind: "link",
                to: "/admin/trading",
                icon: "gears",
                label: t("TradingAdmin"),
                component: "TradingAdmin",
                id: "admin/trading",
                name: t("TradingAdmin"),
                config: {
                  route: "/admin/trading",
                },
                isSubmenuItem: true,
                parentIds: ["admin"],
                searchText: t("TradingAdmin"),
              },
              showMarketControl && {
                id: "admin.marketControl",
                kind: "submenu",
                label: t("MarketControl"),
                icon: "sliders",
                parentIds: ["admin"],
                groups: [
                  {
                    label: "",
                    items: [
                      {
                        kind: "button",
                        icon: "toggle-on",
                        label: t("OpenMarkets"),
                        onActivate: handleOpenMarkets,
                        isSubmenuItem: true,
                        parentIds: ["admin", "admin.marketControl"],
                        searchText: t("OpenMarkets"),
                      },
                      {
                        kind: "button",
                        icon: "toggle-off",
                        label: t("CloseMarkets"),
                        onActivate: handleCloseMarkets,
                        isSubmenuItem: true,
                        parentIds: ["admin", "admin.marketControl"],
                        searchText: t("CloseMarkets"),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
  ].filter(Boolean);

  const utilityItems = [
    {
      id: "userprofile",
      kind: "link",
      label: login,
      icon: "user",
      to: "/profile",
      component: "UserProfile",
      name: t("UserProfile"),
      config: { route: "/profile" },
      activationMatchers: [(pathname) => pathname.startsWith("/profile")],
      searchText: `${login} ${t("UserProfile")}`,
    },
    {
      id: "about",
      kind: "button",
      label: t("About"),
      icon: "info-circle",
      onActivate: handleShowAboutModel,
    },
    {
      id: "logout",
      kind: "button",
      label: t("Logout"),
      icon: "sign-out",
      onActivate: handleLogout,
    },
  ];

  return { topLevelItems, utilityItems };
};

export const deriveNavigationState = (
  { topLevelItems = [], utilityItems = [] },
  pathname = "",
) => {
  const activeMenuItems = new Set();
  const expandedMenuIds = new Set();
  const navigationItems = [...topLevelItems, ...utilityItems];

  navigationItems.forEach((item) => {
    visitNavigationItem(item, pathname, activeMenuItems, expandedMenuIds);
  });

  return {
    activeMenuItems,
    routeExpandedMenus: Array.from(expandedMenuIds),
  };
};

export default buildNavigationModel;
